export const APPLE_WEB_SIGNIN_MESSAGE_TYPE = 'beoflow-apple-signin'
export const APPLE_WEB_SIGNIN_RESUME_KEY = 'apple-web-signin'
export const APPLE_WEB_SIGNIN_STATE_STORAGE_KEY = 'wizard.apple-signin-state'
export const APPLE_WEB_SIGNIN_STATE_BACKUP_STORAGE_KEY = 'wizard.apple-signin-state.backup'
export const APPLE_WEB_SIGNIN_BROADCAST_KEY = 'wizard.apple-signin-broadcast'
export const APPLE_WEB_SIGNIN_HANDOFF_URL_STORAGE_KEY = 'wizard.apple-signin-handoff-url'
export const APPLE_WEB_SIGNIN_PENDING_PAYLOAD_KEY = 'apple-web-signin.pending-payload'
const APPLE_WEB_SIGNIN_BROADCAST_CHANNEL = 'wizard.apple-signin-broadcast'
const APPLE_WEB_SIGNIN_HANDOFF_URL_TTL_MS = 10 * 60 * 1000
const APPLE_WEB_SIGNIN_PENDING_PAYLOAD_TTL_MS = 10 * 60 * 1000

export interface AppleWebSigninMessagePayload {
  type: typeof APPLE_WEB_SIGNIN_MESSAGE_TYPE
  state?: string
  code?: string
  id_token?: string
  user?: string
  error?: string
  error_description?: string
}

interface AppleWebSigninStatePayload {
  nonce: string
  returnPath: string
}

function normalizeAppleWebSigninPayload(
  candidate: Partial<AppleWebSigninMessagePayload> | null | undefined,
): AppleWebSigninMessagePayload | null {
  if (!candidate || candidate.type !== APPLE_WEB_SIGNIN_MESSAGE_TYPE) {
    return null
  }

  return {
    type: APPLE_WEB_SIGNIN_MESSAGE_TYPE,
    state: typeof candidate.state === 'string' ? candidate.state : undefined,
    code: typeof candidate.code === 'string' ? candidate.code : undefined,
    id_token: typeof candidate.id_token === 'string' ? candidate.id_token : undefined,
    user: typeof candidate.user === 'string' ? candidate.user : undefined,
    error: typeof candidate.error === 'string' ? candidate.error : undefined,
    error_description: typeof candidate.error_description === 'string' ? candidate.error_description : undefined,
  }
}

export function getAppleRedirectOrigin(redirectUri: string): string {
  try {
    return new URL(redirectUri).origin
  } catch {
    return ''
  }
}

export function parseAppleWebSigninMessage(
  event: MessageEvent<unknown>,
  expectedOrigin: string,
): AppleWebSigninMessagePayload | null {
  if (!expectedOrigin || event.origin !== expectedOrigin) {
    return null
  }

  if (typeof event.data !== 'object' || event.data === null) {
    return null
  }

  const candidate = event.data as Record<string, unknown>
  return normalizeAppleWebSigninPayload(candidate as Partial<AppleWebSigninMessagePayload>)
}

export function createAppleSigninState(returnPath: string): string {
  const payload: AppleWebSigninStatePayload = {
    nonce: crypto.randomUUID(),
    returnPath: returnPath.startsWith('/') ? returnPath : `/${returnPath}`,
  }
  return encodeURIComponent(JSON.stringify(payload))
}

export function parseAppleSigninState(state?: string): AppleWebSigninStatePayload | null {
  if (!state) return null

  try {
    const parsed = JSON.parse(decodeURIComponent(state)) as Partial<AppleWebSigninStatePayload>
    if (typeof parsed.nonce !== 'string' || typeof parsed.returnPath !== 'string') {
      return null
    }
    return {
      nonce: parsed.nonce,
      returnPath: parsed.returnPath,
    }
  } catch {
    return null
  }
}

export function appleSigninStatesMatch(left?: string, right?: string): boolean {
  if (!left || !right) return false
  if (left === right) return true

  const parsedLeft = parseAppleSigninState(left)
  const parsedRight = parseAppleSigninState(right)

  if (!parsedLeft || !parsedRight) return false

  return parsedLeft.nonce === parsedRight.nonce && parsedLeft.returnPath === parsedRight.returnPath
}

export function storeAppleSigninState(state: string) {
  try {
    sessionStorage.setItem(APPLE_WEB_SIGNIN_STATE_STORAGE_KEY, state)
  } catch {
    // Ignore session storage failures and rely on the local storage backup.
  }

  try {
    localStorage.setItem(APPLE_WEB_SIGNIN_STATE_BACKUP_STORAGE_KEY, state)
  } catch {
    // Ignore local storage failures. Session storage may still be available.
  }
}

export function getStoredAppleSigninState(): string | null {
  try {
    const sessionValue = sessionStorage.getItem(APPLE_WEB_SIGNIN_STATE_STORAGE_KEY)
    if (sessionValue) return sessionValue
  } catch {
    // Ignore session storage failures and fall back to local storage.
  }

  try {
    const backupValue = localStorage.getItem(APPLE_WEB_SIGNIN_STATE_BACKUP_STORAGE_KEY)
    if (backupValue) return backupValue
  } catch {
    // Ignore local storage failures and fall through to null.
  }

  return null
}

export function clearStoredAppleSigninState() {
  try {
    sessionStorage.removeItem(APPLE_WEB_SIGNIN_STATE_STORAGE_KEY)
  } catch {
    // Ignore session storage failures.
  }

  try {
    localStorage.removeItem(APPLE_WEB_SIGNIN_STATE_BACKUP_STORAGE_KEY)
  } catch {
    // Ignore local storage failures.
  }
}

export function storeAppleSigninHandoffUrl(url: string) {
  const payload = JSON.stringify({
    url,
    storedAt: Date.now(),
  })

  try {
    sessionStorage.setItem(APPLE_WEB_SIGNIN_HANDOFF_URL_STORAGE_KEY, payload)
  } catch {
    // Ignore session storage failures and rely on local storage backup.
  }

  try {
    localStorage.setItem(APPLE_WEB_SIGNIN_HANDOFF_URL_STORAGE_KEY, payload)
  } catch {
    // Ignore local storage failures. Session storage may still be available.
  }
}

export function getStoredAppleSigninHandoffUrl(): string | null {
  const readPayload = (rawValue: string | null) => {
    if (!rawValue) return null

    try {
      const parsed = JSON.parse(rawValue) as { url?: unknown; storedAt?: unknown }
      if (typeof parsed.url !== 'string' || typeof parsed.storedAt !== 'number') return null
      if (Date.now() - parsed.storedAt > APPLE_WEB_SIGNIN_HANDOFF_URL_TTL_MS) return null
      return parsed.url
    } catch {
      return null
    }
  }

  try {
    const sessionValue = readPayload(sessionStorage.getItem(APPLE_WEB_SIGNIN_HANDOFF_URL_STORAGE_KEY))
    if (sessionValue) return sessionValue
  } catch {
    // Ignore session storage failures and fall back to local storage.
  }

  try {
    const backupValue = readPayload(localStorage.getItem(APPLE_WEB_SIGNIN_HANDOFF_URL_STORAGE_KEY))
    if (backupValue) return backupValue
  } catch {
    // Ignore local storage failures and fall through to null.
  }

  return null
}

export function clearStoredAppleSigninHandoffUrl() {
  try {
    sessionStorage.removeItem(APPLE_WEB_SIGNIN_HANDOFF_URL_STORAGE_KEY)
  } catch {
    // Ignore session storage failures.
  }

  try {
    localStorage.removeItem(APPLE_WEB_SIGNIN_HANDOFF_URL_STORAGE_KEY)
  } catch {
    // Ignore local storage failures.
  }
}

export function parseAppleWebSigninResumePayload(hash: string): AppleWebSigninMessagePayload | null {
  if (!hash) return null

  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const encodedPayload = params.get(APPLE_WEB_SIGNIN_RESUME_KEY)
  if (!encodedPayload) return null

  try {
    const parsed = JSON.parse(encodedPayload) as Partial<AppleWebSigninMessagePayload>
    return normalizeAppleWebSigninPayload(parsed)
  } catch {
    return null
  }
}

/** Move the credential-bearing callback fragment out of the URL before
 * Sentry/PostHog initialize. The component consumes it after React mounts. */
export function consumeAppleWebSigninPayloadFromUrl(): AppleWebSigninMessagePayload | null {
  const payload = parseAppleWebSigninResumePayload(window.location.hash)
  if (!payload) return null
  try {
    sessionStorage.setItem(APPLE_WEB_SIGNIN_PENDING_PAYLOAD_KEY, JSON.stringify({
      payload,
      storedAt: Date.now(),
    }))
  } catch {
    return null
  }
  window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
  return payload
}

export function takePendingAppleWebSigninPayload(): AppleWebSigninMessagePayload | null {
  try {
    const value = sessionStorage.getItem(APPLE_WEB_SIGNIN_PENDING_PAYLOAD_KEY)
    sessionStorage.removeItem(APPLE_WEB_SIGNIN_PENDING_PAYLOAD_KEY)
    if (!value) return null
    const stored = JSON.parse(value) as { payload?: Partial<AppleWebSigninMessagePayload>; storedAt?: unknown }
    if (typeof stored.storedAt !== 'number' || Date.now() - stored.storedAt > APPLE_WEB_SIGNIN_PENDING_PAYLOAD_TTL_MS) {
      return null
    }
    return normalizeAppleWebSigninPayload(stored.payload)
  } catch {
    return null
  }
}

function parseAppleWebSigninBroadcastPayload(value: string): AppleWebSigninMessagePayload | null {
  try {
    return normalizeAppleWebSigninPayload(JSON.parse(value) as Partial<AppleWebSigninMessagePayload>)
  } catch {
    return null
  }
}

export function broadcastAppleWebSigninPayload(payload: AppleWebSigninMessagePayload) {
  const serialized = JSON.stringify(payload)

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(APPLE_WEB_SIGNIN_BROADCAST_CHANNEL)
      channel.postMessage(serialized)
      channel.close()
    }
  } catch {
    // Ignore BroadcastChannel failures and rely on storage-event fallback.
  }

  try {
    localStorage.setItem(APPLE_WEB_SIGNIN_BROADCAST_KEY, serialized)
    localStorage.removeItem(APPLE_WEB_SIGNIN_BROADCAST_KEY)
  } catch {
    // Ignore storage failures. The direct tab can still continue locally.
  }
}

export function subscribeToAppleWebSigninBroadcast(
  callback: (payload: AppleWebSigninMessagePayload) => void,
): () => void {
  let channel: BroadcastChannel | null = null

  const handlePayload = (value: unknown) => {
    if (typeof value !== 'string') return
    const payload = parseAppleWebSigninBroadcastPayload(value)
    if (payload) callback(payload)
  }

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(APPLE_WEB_SIGNIN_BROADCAST_CHANNEL)
      channel.onmessage = (event) => {
        handlePayload(event.data)
      }
    }
  } catch {
    channel = null
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== APPLE_WEB_SIGNIN_BROADCAST_KEY || !event.newValue) return
    handlePayload(event.newValue)
  }

  window.addEventListener('storage', handleStorage)

  return () => {
    window.removeEventListener('storage', handleStorage)
    if (channel) {
      channel.close()
    }
  }
}

export function extractAppleUserEmail(userPayload?: string): string | undefined {
  if (!userPayload) return undefined

  try {
    const parsed = JSON.parse(userPayload) as { email?: unknown }
    return typeof parsed.email === 'string' ? parsed.email : undefined
  } catch {
    return undefined
  }
}
